import { isKnownTimezone } from '../../../lib/forms/timezones.js';
import { z } from 'zod';

/**
 * The PMS credentials module's contracts.
 *
 * The detail shape is strongly typed; the form shape is all strings so it
 * can pass through the shared payload builder. Booleans and the module-type
 * array are normalised to strings in the form and converted back on submit.
 */

/** PMS provider options shown in the form and list filters. */
export const PMS_PROVIDER_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'RMS', label: 'RMS' },
];

export const PMS_STATUS_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

export const PMS_CREDENTIAL_DEFAULTS: PmsCredentialFormInput = {
  name: '',
  pmsProvider: 'RMS',
  travelAgentId: '',
  marketSegmentId: '',
  bookingSourceId: '',
  partialReservationExpireTime: '',
  useTestEnvironment: 'false',
  status: 'ACTIVE',
  baseUrl: '',
  agentId: '',
  agentSecret: '',
  clientId: '',
  clientSecret: '',
  rateGridAgentId: '',
  timezone: '',
  masterRateTravelAgentId: '',
  reservationTravelAgentId: '',
  moduleType: 'distribution',
};

const MAX_NAME_LENGTH = 255;
const MAX_URL_LENGTH = 512;
const MAX_SECRET_LENGTH = 255;
const MAX_TIMEZONE_LENGTH = 64;
const MAX_ID_LENGTH = 20;
const MAX_EXPIRE_TIME_LENGTH = 10;
const MAX_MODULE_TYPE_LENGTH = 255;

const POSITIVE_INTEGER_REGEX = /^\d+$/u;
const MODULE_TYPE_REGEX = /^[a-zA-Z0-9-,\s]*$/u;
const URL_REGEX = /^https?:\/\/.+/u;

const positiveIntegerString = (field: string, max: number): z.ZodString =>
  z
    .string()
    .trim()
    .regex(POSITIVE_INTEGER_REGEX, `${field} must be a number`)
    .max(max, `${field} is too long`);

const optionalPositiveIntegerString = (
  field: string,
  max: number,
): z.ZodUnion<[z.ZodLiteral<''>, z.ZodString]> =>
  z.union([
    z.literal(''),
    z
      .string()
      .trim()
      .regex(POSITIVE_INTEGER_REGEX, `${field} must be a number`)
      .max(max, `${field} is too long`),
  ]);

const booleanString = z.enum(['true', 'false']);

/** One row in the PMS credentials list. */
export const pmsCredentialRow = z.object({
  id: z.string(),
  name: z.string().optional(),
  pmsProvider: z.string().optional(),
  status: z.string().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type PmsCredentialRow = z.infer<typeof pmsCredentialRow>;

/** The full credential, as the detail screen and edit form read it. */
export const pmsCredentialDetail = z.object({
  id: z.string(),
  name: z.string(),
  pmsProvider: z.enum(['RMS']),
  travelAgentId: z.number().int(),
  marketSegmentId: z.number().int(),
  bookingSourceId: z.number().int(),
  partialReservationExpireTime: z.number().int(),
  useTestEnvironment: z.boolean(),
  status: z.enum(['ACTIVE', 'INACTIVE']),
  baseUrl: z.string().nullable(),
  agentId: z.number().int(),
  agentSecret: z.string(),
  clientId: z.number().int(),
  clientSecret: z.string(),
  rateGridAgentId: z.number().int().nullable(),
  timezone: z.string().nullable(),
  masterRateTravelAgentId: z.number().int().nullable(),
  reservationTravelAgentId: z.number().int().nullable(),
  moduleType: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
  version: z.number(),
});

export type PmsCredentialDetail = z.infer<typeof pmsCredentialDetail>;

/** The identifier returned by a create request. */
export const createdPmsCredential = z.object({ id: z.string() });
export type CreatedPmsCredential = z.infer<typeof createdPmsCredential>;

/** What the create and edit forms collect. */
export const pmsCredentialFormInput = z.object({
  name: z.string().trim().min(1, 'Connection name is required').max(MAX_NAME_LENGTH),
  pmsProvider: z.enum(['RMS']),
  travelAgentId: positiveIntegerString('Travel agent ID', MAX_ID_LENGTH),
  marketSegmentId: positiveIntegerString('Market segment ID', MAX_ID_LENGTH),
  bookingSourceId: positiveIntegerString('Booking source ID', MAX_ID_LENGTH),
  partialReservationExpireTime: positiveIntegerString(
    'Partial reservation expire time',
    MAX_EXPIRE_TIME_LENGTH,
  ),
  useTestEnvironment: booleanString,
  status: z.enum(['ACTIVE', 'INACTIVE']),
  baseUrl: z
    .string()
    .trim()
    .min(1, 'Base URL is required')
    .max(MAX_URL_LENGTH)
    .regex(URL_REGEX, 'Base URL must start with http:// or https://'),
  agentId: positiveIntegerString('Agent ID', MAX_ID_LENGTH),
  agentSecret: z.string().trim().min(1, 'Agent secret is required').max(MAX_SECRET_LENGTH),
  clientId: positiveIntegerString('Client ID', MAX_ID_LENGTH),
  clientSecret: z.string().trim().min(1, 'Client secret is required').max(MAX_SECRET_LENGTH),
  rateGridAgentId: optionalPositiveIntegerString('Rate grid agent ID', MAX_ID_LENGTH),
  timezone: z
    .string()
    .min(1, 'Timezone is required')
    .max(MAX_TIMEZONE_LENGTH)
    .refine(isKnownTimezone, 'Choose a valid IANA timezone'),
  masterRateTravelAgentId: optionalPositiveIntegerString(
    'Master rate travel agent ID',
    MAX_ID_LENGTH,
  ),
  reservationTravelAgentId: optionalPositiveIntegerString(
    'Reservation travel agent ID',
    MAX_ID_LENGTH,
  ),
  moduleType: z
    .string()
    .trim()
    .max(MAX_MODULE_TYPE_LENGTH)
    .regex(MODULE_TYPE_REGEX, 'Use comma-separated module names'),
});

export type PmsCredentialFormInput = z.infer<typeof pmsCredentialFormInput>;
